import {
  type QueryClient,
  type QueryStatus,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useMemo } from 'react';

import {
  GET_SCREENER_DATA,
  ScreenerDimension,
  type ScreenerReq,
  type ScreenerRespEntity,
} from '@revelio/data-access';
import type { TimelineData } from '@revelio/replots';

import type { CompanySearchApiResult } from '../../../../../shared/api/fetchCompanySearchResults';
import { executeSavedQuery } from '../../../api/saved-queries-api';
import {
  buildSubsidiaryLineage,
  type LineageRow,
} from '../../Chart/Timeline/build-subsidiary-lineage';
import { type DataProviderChildren, DataProviderQueryStatus } from '../types';
import {
  combineQueryStatus,
  useUrqlQueryWithStatus,
} from '../utils/data-provider-results-utils';
import {
  allSubsidiariesQuery,
  companyQuery,
  toSubsidiaryQueryStatus,
  useCompany,
} from './company-mapper-queries';
import { mapWithConcurrency } from './fetch-subsidiary-tree';
import { usePrimaryCompany } from './use-primary-company';

const SUBSIDIARY_LINEAGE_SAVED_QUERY_ID = 'subsidiaries-lineage';
const SCREENER_LIMIT = 1000;
const UNMAPPED_LOOKUP_LIMIT = 200;
const UNMAPPED_LOOKUP_CONCURRENCY = 6;

export type SubsidiaryLineage = {
  rcid: number;
  rootName: string;
  data: TimelineData;
  /** Rows the timeline could date; zero means the group has no dated links. */
  datedCount: number;
};

/**
 * The screener only matches a company's ULTIMATE parent, so the request names
 * both the selected company and the top of its chain.
 */
const foundingRequest = (parentRcids: number[]): ScreenerReq => ({
  dim: ScreenerDimension.Company,
  primary_selector: {},
  segments: {},
  pagination: { limit: SCREENER_LIMIT, offset: 0 },
  entity_info_selectors: {
    company_info_selectors: { is_subsidiary_of_rcids: parentRcids },
  },
});

const lineageRowsQuery = (rcid: number) => ({
  queryKey: ['company-subsidiary-lineage-rows', rcid],
  queryFn: async () =>
    ((await executeSavedQuery(SUBSIDIARY_LINEAGE_SAVED_QUERY_ID, { rcid })) ??
      []) as LineageRow[],
  staleTime: Number.POSITIVE_INFINITY,
  retry: false,
  meta: { skipGlobalErrorHandling: true },
});

const combineLineageRows = (
  results: { data?: LineageRow[]; status: QueryStatus }[]
) => ({
  rows: results.map((result) => result.data),
  statuses: results.map((result) => toSubsidiaryQueryStatus(result.status)),
});

export const useLineageRows = (rcids: number[]) =>
  useQueries({
    queries: rcids.map((rcid) => lineageRowsQuery(rcid)),
    combine: combineLineageRows,
  });

const unmappedRcids = (
  rows: LineageRow[],
  companies: CompanySearchApiResult[]
): number[] => {
  const known = new Set(companies.map((company) => company.rcid));
  const ids = [...rows]
    .sort((a, b) => Number(a.END_DATE !== null) - Number(b.END_DATE !== null))
    .map((row) => Number(row.ID))
    .filter((id) => Number.isFinite(id) && !known.has(id));
  return [...new Set(ids)].slice(0, UNMAPPED_LOOKUP_LIMIT);
};

type LineageSources = {
  rows: LineageRow[];
  companies: CompanySearchApiResult[];
};

const lineageSourcesQuery = (queryClient: QueryClient, rcid: number) => ({
  queryKey: ['company-subsidiary-lineage', rcid],
  queryFn: async ({
    signal,
  }: {
    signal: AbortSignal;
  }): Promise<LineageSources> => {
    const [lineageRows, companies] = await Promise.all([
      queryClient.fetchQuery(lineageRowsQuery(rcid)),
      queryClient.fetchQuery(allSubsidiariesQuery(rcid)),
    ]);
    const departed = await mapWithConcurrency(
      unmappedRcids(lineageRows, companies),
      UNMAPPED_LOOKUP_CONCURRENCY,
      (id) =>
        signal.aborted
          ? Promise.resolve(null)
          : queryClient.fetchQuery(companyQuery(id)).catch(() => null)
    );
    if (signal.aborted) throw signal.reason;
    return {
      rows: lineageRows,
      companies: [
        ...companies,
        ...departed.filter(
          (company): company is CompanySearchApiResult => company !== null
        ),
      ],
    };
  },
  staleTime: Number.POSITIVE_INFINITY,
  retry: false,
  meta: { skipGlobalErrorHandling: true },
});

const combineLineageSources = (
  results: { data?: LineageSources; status: QueryStatus }[]
) => ({
  sources: results.map((result) => result.data),
  statuses: results.map((result) => toSubsidiaryQueryStatus(result.status)),
});

export const useGroupLineages = (
  companies: { rcid: number; name: string }[]
) => {
  const queryClient = useQueryClient();
  const { sources, statuses } = useQueries({
    queries: companies.map((company) =>
      lineageSourcesQuery(queryClient, company.rcid)
    ),
    combine: combineLineageSources,
  });
  const lineages = useMemo(
    () =>
      companies.map((company, index) => {
        const source = sources[index];
        return source === undefined
          ? undefined
          : buildSubsidiaryLineage({
              rows: source.rows,
              root: company,
              companies: source.companies,
            });
      }),
    [companies, sources]
  );
  return { lineages, status: combineQueryStatus(statuses) };
};

const toFoundedYears = (
  entities: ScreenerRespEntity[]
): Map<number, number> => {
  const years = new Map<number, number>();
  for (const entity of entities) {
    const year = entity.entity_info?.company_info?.year_founded;
    if (typeof year === 'number' && Number.isFinite(year)) {
      years.set(entity.entity_id, year);
    }
  }
  return years;
};

/**
 * The subsidiaries timeline for the primary company chip: FactSet's dated
 * parent links (a saved Snowflake query) joined to company-mapper for names
 * and sizes, and to the screener for founding years — the stretch a company
 * ran on its own before it was acquired. Like the icicle it ignores every
 * other page filter.
 */
export const SubsidiaryLineageDataProvider = ({
  children,
}: {
  children: DataProviderChildren<SubsidiaryLineage | null>;
}) => {
  const company = usePrimaryCompany();
  const rcid = company?.rcid ?? null;
  const rootName = company?.name ?? '';
  const queryClient = useQueryClient();

  const { data, status, error } = useQuery({
    ...lineageSourcesQuery(queryClient, rcid as number),
    enabled: rcid !== null,
  });

  const record = useCompany(rcid);
  const ultimateParentRcid = record.data?.ult_parent_rcid ?? null;
  const variables = useMemo(
    () => ({
      filters: foundingRequest(
        [...new Set([rcid, ultimateParentRcid])].filter(
          (id): id is number => id !== null
        )
      ),
    }),
    [rcid, ultimateParentRcid]
  );
  const [screener] = useUrqlQueryWithStatus<
    { screener: { entities: ScreenerRespEntity[] } },
    { filters: ScreenerReq }
  >({
    query: GET_SCREENER_DATA,
    variables,
    pause: rcid === null || record.status === 'pending',
  });
  const entities = screener.data?.screener?.entities;
  // Founding years are a refinement: a screener failure must not take the
  // chart down with it, so the timeline then simply starts every track at
  // its join.
  const foundingSettled =
    entities !== undefined ||
    screener.status === DataProviderQueryStatus.Error ||
    record.status === 'error';
  const foundedYearByRcid = useMemo(
    () => (entities === undefined ? undefined : toFoundedYears(entities)),
    [entities]
  );

  const lineage = useMemo((): SubsidiaryLineage | undefined => {
    if (rcid === null || data === undefined || !foundingSettled) {
      return undefined;
    }
    return {
      rcid,
      rootName,
      data: buildSubsidiaryLineage({
        rows: data.rows,
        root: { rcid, name: rootName },
        companies: data.companies,
        ...(foundedYearByRcid !== undefined && { foundedYearByRcid }),
      }),
      datedCount: data.rows.filter((row) => row.START_DATE !== null).length,
    };
  }, [data, rcid, rootName, foundingSettled, foundedYearByRcid]);

  if (rcid === null) {
    return children({ data: null, status: DataProviderQueryStatus.Success });
  }

  const combined = combineQueryStatus([
    toSubsidiaryQueryStatus(status),
    foundingSettled
      ? DataProviderQueryStatus.Success
      : DataProviderQueryStatus.Fetching,
  ]);
  return children({
    data: lineage,
    status: combined,
    ...(error !== null && { error }),
  });
};
